import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import './style.css';

/** Fixed widths per row — random widths in render caused layout to jump on every re-render. */
const BUBBLE_WIDTH_PCTS = [52, 68, 45, 72, 58, 64, 50, 70];
const BUBBLE_WIDTH_PCTS_V2 = [42, 56, 38, 62, 48, 54, 40, 58];

const skeletonSx = {
  bubbleAgent: {
    borderRadius: '16px',
    bgcolor: 'rgba(0, 123, 255, 0.08)',
    transform: 'none',
  },
  bubbleAgentV2: {
    borderRadius: '8px',
    bgcolor: 'rgba(214, 237, 255, 0.9)',
    transform: 'none',
  },
  bubbleUser: {
    borderRadius: '16px',
    bgcolor: 'rgba(0, 0, 0, 0.06)',
    transform: 'none',
  },
  bubbleUserV2: {
    borderRadius: '8px',
    bgcolor: 'rgba(241, 243, 245, 1)',
    transform: 'none',
  },
  avatar: { mr: 1, bgcolor: 'rgba(0, 0, 0, 0.06)', flexShrink: 0 },
  avatarV2: { bgcolor: 'rgba(0, 0, 0, 0.06)', flexShrink: 0 },
  meta: { mt: 0.5, bgcolor: 'rgba(0, 0, 0, 0.06)' },
};

const ChatMessageSkeleton = ({ messageCount = 6, variant }) => {
  const isV2 = variant === 'v2';
  const widths = isV2 ? BUBBLE_WIDTH_PCTS_V2 : BUBBLE_WIDTH_PCTS;
  const bubbleWidths = useMemo(
    () =>
      Array.from({ length: messageCount }, (_, i) => `${widths[i % widths.length]}%`),
    [messageCount, widths]
  );

  const isAgentMessage = (index) => {
    if (index === 0) return true;
    return index % 2 === 0;
  };

  return (
    <div
      className={isV2 ? 'chatv2-message-skeleton' : undefined}
      style={isV2 ? { width: '100%', boxSizing: 'border-box' } : undefined}
    >
      {Array.from({ length: messageCount }).map((_, index) => {
        const isAgent = isAgentMessage(index);
        if (isV2) {
          return (
            <div
              key={index}
              className="chatv2-message-skeleton-row"
              style={{
                display: 'flex',
                flexDirection: isAgent ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                width: '100%',
                marginBottom: 16,
                gap: 8,
                boxSizing: 'border-box',
              }}
            >
              <Skeleton
                variant="circular"
                width={32}
                height={32}
                animation="pulse"
                sx={skeletonSx.avatarV2}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isAgent ? 'flex-end' : 'flex-start',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Skeleton
                  variant="rounded"
                  width={bubbleWidths[index]}
                  height={isAgent ? 64 : 88}
                  animation="pulse"
                  sx={isAgent ? skeletonSx.bubbleAgentV2 : skeletonSx.bubbleUserV2}
                />
                <Skeleton
                  variant="text"
                  width={48}
                  height={14}
                  animation="pulse"
                  sx={{
                    ...skeletonSx.meta,
                    ml: isAgent ? 'auto' : 0,
                    mr: isAgent ? 0 : 'auto',
                  }}
                />
              </div>
            </div>
          );
        }

        return (
          <div
            key={index}
            className={`Message-Container ${isAgent ? 'agent-message' : 'user-message'}`}
            style={{ marginBottom: '16px' }}
          >
            {!isAgent && (
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                animation="pulse"
                sx={skeletonSx.avatar}
              />
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isAgent ? 'flex-end' : 'flex-start',
                width: '100%',
              }}
            >
              <Skeleton
                variant="rounded"
                width={bubbleWidths[index]}
                height={56}
                animation="pulse"
                sx={isAgent ? skeletonSx.bubbleAgent : skeletonSx.bubbleUser}
              />

              <Skeleton
                variant="text"
                width={50}
                height={16}
                animation="pulse"
                sx={{
                  ...skeletonSx.meta,
                  ml: isAgent ? 'auto' : '0',
                  mr: isAgent ? '0' : 'auto',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChatMessageSkeleton;
